import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addDonation,
  addROI,
  createRemoteBillingAddress,
  deleteDonationOrderItem,
  deleteOrderItem,
  deleteRemoteRegistrationOrder,
  getRegistrationOrder,
  getRegistrationOrderItem,
  payOrder,
  removeROI,
  saveCustomer,
  updateDonationOrderItem,
  updateInProgressPlayer,
  updateInProgressSponsor,
  updateInProgressTeam,
  updateRemoteBillingAddress,
  updateRemoteRegistrationOrder,
} from "../api/registrationAccess";
import {
  addPackageToRegistrationOrder,
  decrementOrderItem,
  RegistrationOrder,
  removeOrderItem,
  stripOrderOfPaymentDetails,
  updateRegisteredPlayer,
  updateRegisteredTeam,
  updateRegistrationOrder,
  updateRegistrationSponsor,
  updateSponsorForm,
  updateTeamForm,
} from "../models/Registration";
import {
  applyDiscount,
  calculateOrderPricing,
  createAndPay,
  internationalCardCheck,
} from "../api/localRegistration";
import {
  addDonationToOrderThunkResponse,
  addTournamentPackageToOrderThunkResponse,
  applyDiscountThunkResponse,
  calculatePricingRespose,
  createAndPayThunkResponse,
  createBillingAddressThunkResponse,
  deleteRemoteRegistrationOrderThunkResponse,
  deleteTournamentRegistrationOrderItemThunkresponse,
  getAccessCodeThunkResponse,
  getPublicTournamentDetailsResponse,
  getRegistrationOrderThunkResponse,
  internationalCardCheckThunkResponse,
  payOrderThunkResponse,
  removeDonationOrderItemThunkResponse,
  removeTournamentPackageFromOrderThunkResponse,
  saveCustomerThunkResponse,
  updateDonationOrderItemThunkResponse,
  updateInProgressPlayerThunkResponse,
  updateInProgressSponsorThunkResponse,
  updateInProgressTeamThunkResponse,
  updateLocalCustomer,
  updateRemoteRegistrationOrderThunkResponse,
} from "../api/registrationThunkResponses";

//replace all occurences of template with the name of your new slice
export const registrationOrderSlice = createSlice({
  name: "registrationOrder",
  initialState: {
    registrationOrder: {},
    registrationCustomer: {},
    paymentDetails: {
      registrantCoversFees: true,
      savePayment: false,
      accountType: "checking",
      nameType: "business",
    },
    completedOrder: {},
    tournament: {},
    notifications: [],
    loading: [],
  },
  //be sure to export any reducers on line 65
  reducers: {
    setBillingAddress: (state, action) => {
      const order = {
        ...state.registrationOrder,
        billingAddress: action.payload,
      };
      state.registrationOrder = order;
    },
    applyAccessCodes: (state, action) => {
      if (action?.payload?.length === 0) {
        const order = {
          ...state.registrationOrder,
          accessIdentifier: null,
          accessValidated: false,
          accessEmail: null,
          accessGroupID: null,
          accessGroupMemberID: null,
        };
        updateRegistrationOrder(state, order);
      }
    },
    clearPayment: (state, action) => {
      const update = stripOrderOfPaymentDetails(state.registrationOrder);
      updateRegistrationOrder(state, update);
    },
    clearTokens: (state) => {
      const update = {
        ...state.registrationOrder,
        globalPaymentToken: null,
        stripePaymentToken: null,
        saveCard: false,
      };
      updateRegistrationOrder(state, update);
    },
    updateOrder: (state, action) => {
      updateRegistrationOrder(state, action.payload);
    },
    addPackageToOrder: (state, action) => {
      let order = { ...state.registrationOrder };
      addPackageToRegistrationOrder(action.payload, order);
      updateRegistrationOrder(state, order);
    },
    removePackageItemFromOrder: (state, action) => {
      let order = { ...state.registrationOrder };
      removeOrderItem(action.payload, order);
      updateRegistrationOrder(state, order);
    },
    decrementItemQuantity: (state, action) => {
      let order = { ...state.registrationOrder };
      decrementOrderItem(action.payload, order);
      updateRegistrationOrder(state, order);
    },
    updateCustomer: (state, action) => {
      updateLocalCustomer(state, action.payload, state.tournament);
    },
    completeOrder: (state, action) => {
      state.completedOrder = { ...state.registrationOrder };
      updateRegistrationOrder(state, {
        ...RegistrationOrder,
        tournament: state.tournament,
      });
    },
    updateOrderForm: (state, action) => {
      const update = { ...state.registrationOrder };
      update.customFieldResponses = action.payload;
      updateRegistrationOrder(state, update);
    },
    updateTeamOrderForm: (state, action) => {
      const registrationOrder = { ...state.registrationOrder };
      updateRegistrationOrder(
        state,
        updateTeamForm(registrationOrder, action?.payload)
      );
    },
    updateSponsorOrderForm: (state, action) => {
      const registrationOrder = { ...state?.registrationOrder };
      updateRegistrationOrder(
        state,
        updateSponsorForm(registrationOrder, action?.payload)
      );
    },
    updatePlayer: (state, action) => {
      const registrationOrder = updateRegisteredPlayer(
        { ...state.registrationOrder },
        action
      );
      updateRegistrationOrder(state, registrationOrder);
    },
    updateTeam: (state, action) => {
      const registrationOrder = updateRegisteredTeam(
        { ...state.registrationOrder },
        action
      );
      updateRegistrationOrder(state, registrationOrder);
    },
    updateSponsor: (state, action) => {
      const registrationOrder = updateRegistrationSponsor(
        { ...state.registrationOrder },
        action
      );
      updateRegistrationOrder(state, registrationOrder);
    },
    addNotification: (state, action) => {
      state.notifications.push(action.payload);
    },
    clearNotifications: (state, action) => {
      state.notifications = [];
    },
    setPaymentDetails: (state, action) => {
      state.paymentDetails = action.payload;
    },
  },
  extraReducers: (builder) => {
    getPublicTournamentDetailsResponse(builder);
    //order
    updateRemoteRegistrationOrderThunkResponse(builder);
    getRegistrationOrderThunkResponse(builder);
    addTournamentPackageToOrderThunkResponse(builder);
    removeTournamentPackageFromOrderThunkResponse(builder);
    deleteTournamentRegistrationOrderItemThunkresponse(builder);
    saveCustomerThunkResponse(builder);
    addDonationToOrderThunkResponse(builder);
    updateDonationOrderItemThunkResponse(builder);
    removeDonationOrderItemThunkResponse(builder);
    deleteRemoteRegistrationOrderThunkResponse(builder);
    payOrderThunkResponse(builder);
    updateInProgressTeamThunkResponse(builder);
    updateInProgressPlayerThunkResponse(builder);
    updateInProgressSponsorThunkResponse(builder);
    createBillingAddressThunkResponse(builder);
    //access code
    getAccessCodeThunkResponse(builder);
    //old api
    calculatePricingRespose(builder);
    createAndPayThunkResponse(builder);
    applyDiscountThunkResponse(builder);
    internationalCardCheckThunkResponse(builder);
  },
});

//getting data
export const getRegistrationOrderThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/getRegistrationOrderThunk`,
  getRegistrationOrder
);

export const getRegistrationOrderItemThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/getRegistrationOrderItemThunk`,
  getRegistrationOrderItem
);

//order

export const updateOrderThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/updateOrderThunk`,
  updateRemoteRegistrationOrder
);

export const payOrderThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/payOrderThunk`,
  payOrder
);

export const deleteOrderThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/deleteOrderThunk`,
  deleteRemoteRegistrationOrder
);

export const addTournamentPackageToOrderThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/addTournamentPackageToOrderThunk`,
  addROI
);

export const removeTournamentPackageFromOrderThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/removeTournamentPackageFromOrderThunk`,
  removeROI
);

export const deleteTournamentRegistrationOrderItemThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/deleteTournamentRegistrationOrderItemThunk`,
  deleteOrderItem
);

export const saveCustomerThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/saveCustomerThunk`,
  saveCustomer
);

export const addDonationToOrderThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/addDonationToOrderThunk`,
  addDonation
);

export const updateDonationItemThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/updateDonationItemThunk`,
  updateDonationOrderItem
);

export const removeDonationOrderItemThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/removeDonationOrderItemThunk`,
  deleteDonationOrderItem
);

export const updateInProgressSponsorThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/updateInProgressSponsorThunk`,
  updateInProgressSponsor
);

export const updateInProgressTeamThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/updateInProgressTeamThunk`,
  updateInProgressTeam
);

export const updateInProgressPlayerThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/updateInProgressPlayerThunk`,
  updateInProgressPlayer
);

export const updateCustomFieldResponseThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/updateCustomFieldResponseThunk`,
  updateInProgressPlayer
);

export const createBillingAddressThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/createBillingAddressThunk`,
  createRemoteBillingAddress
);

export const updateBillingAddressThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/updateBillingAddressThunk`,
  updateRemoteBillingAddress
);

//old local create and pay

export const internationalCardCheckThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/internationalCardCheckThunk`,
  internationalCardCheck
);

export const calculatePricing = createAsyncThunk(
  `${registrationOrderSlice.name}/calculatePricing`,
  calculateOrderPricing
);

export const createAndPayThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/createAndPaythunk`,
  createAndPay
);

export const applyDiscountThunk = createAsyncThunk(
  `${registrationOrderSlice.name}/applyDiscountThunk`,
  applyDiscount
);

export const {
  removePackageItemFromOrder,
  addNotification,
  clearNotifications,
  addPackageToOrder,
  clearPayment,
  clearTokens,
  updateOrder,
  decrementItemQuantity,
  updateCustomer,
  completeOrder,
  updateOrderForm,
  updatePlayer,
  updateSponsor,
  updateSponsorOrderForm,
  updateTeam,
  updateTeamOrderForm,
  applyAccessCodes,
  setBillingAddress,
  setPaymentDetails
} = registrationOrderSlice.actions;

//export any selectors needed
export const selectRegistrationOrder = (state) =>
  state?.[registrationOrderSlice.name]?.registrationOrder;

export const selectRegistrationCustomer = (state) =>
  state?.[registrationOrderSlice.name]?.registrationOrder?.registrationCustomer;

export const selectNotifications = (state) =>
  state?.[registrationOrderSlice.name]?.notifications;

export const selectLoading = (state) =>
  state?.[registrationOrderSlice.name]?.loading;

export const selectCompletedOrder = (state) =>
  state?.[registrationOrderSlice.name]?.completedOrder;

export const selectPaymentDetails = (state) => state?.[registrationOrderSlice.name]?.paymentDetails

export default registrationOrderSlice.reducer;

//ADD reducer to store.js
